import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';

interface Organization {
  id: string;
  name: string;
  slug: string;
  organizationType: string;
  subscriptionStatus: string;
  planId: string;
  planName?: string;
  logoUrl?: string;
  userRole?: string; // role do usuário autenticado nesta organização
}

interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  isLoading: boolean;
  setCurrentOrganization: (org: Organization) => void;
  refetchOrganizations: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchOrganizations = async () => {
    if (!user?.id) {
      console.log('[OrganizationContext] Usuário não autenticado');
      setOrganizations([]);
      setCurrentOrganization(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      console.log('[OrganizationContext] Buscando organizações para user:', user.id);

      // Buscar organizações do usuário
      const { data: memberData, error: memberError } = await supabase
        .from('organization_members')
        .select(`
          organization_id,
          status,
          roles (name),
          organizations (
            id,
            name,
            slug,
            organization_type,
            subscription_status,
            plan_id,
            logo_url,
            subscription_plans (name)
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'active');

      console.log('[OrganizationContext] Resultado da query:', { memberData, memberError });

      if (memberError) {
        console.error('[OrganizationContext] Erro na query:', memberError);
        throw memberError;
      }

      const orgs: Organization[] = (memberData || [])
        .filter((m: any) => m.organizations)
        .map((m: any) => ({
          id: m.organizations.id,
          name: m.organizations.name,
          slug: m.organizations.slug,
          organizationType: m.organizations.organization_type,
          subscriptionStatus: m.organizations.subscription_status,
          planId: m.organizations.plan_id,
          planName: m.organizations.subscription_plans?.name,
          logoUrl: m.organizations.logo_url,
          userRole: (m.roles as any)?.name,
        }));

      console.log('[OrganizationContext] Organizações processadas:', orgs);
      setOrganizations(orgs);

      // Se já tem uma organização selecionada, mantém
      if (currentOrganization && orgs.some(o => o.id === currentOrganization.id)) {
        console.log('[OrganizationContext] Mantendo organização atual:', currentOrganization.name);
        return;
      }

      // Tentar recuperar organização salva no localStorage
      const savedOrgId = localStorage.getItem('selected_organization_id');
      console.log('[OrganizationContext] Organização salva no localStorage:', savedOrgId);
      
      if (savedOrgId) {
        const savedOrg = orgs.find(o => o.id === savedOrgId);
        if (savedOrg) {
          console.log('[OrganizationContext] Restaurando organização salva:', savedOrg.name);
          setCurrentOrganization(savedOrg);
          return;
        }
      }

      // Selecionar primeira organização automaticamente
      if (orgs.length > 0) {
        console.log('[OrganizationContext] Selecionando primeira organização:', orgs[0].name);
        setCurrentOrganization(orgs[0]);
        localStorage.setItem('selected_organization_id', orgs[0].id);
      } else {
        console.warn('[OrganizationContext] Nenhuma organização encontrada para o usuário');
      }
    } catch (error) {
      console.error('[OrganizationContext] Erro ao buscar organizações:', error);
      setOrganizations([]);
      setCurrentOrganization(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Buscar organizações apenas após auth resolver — evita race condition
  // onde orgLoading=false mas user acabou de ser definido e orgs ainda não foram buscadas
  useEffect(() => {
    if (authLoading) return;
    fetchOrganizations();
  }, [user?.id, authLoading]);

  const handleSetCurrentOrganization = (org: Organization) => {
    console.log('[OrganizationContext] Trocando organização para:', org.name);
    setCurrentOrganization(org);
    localStorage.setItem('selected_organization_id', org.id);
    
    // Navegar para a nova organização mantendo a rota atual
    const pathWithoutOrg = location.pathname.replace(/^\/org\/[^/]+/, '');
    const newPath = `/org/${org.slug}${pathWithoutOrg || ''}`;
    console.log('[OrganizationContext] Navegando para:', newPath);
    navigate(newPath, { replace: true });
  };

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganization,
        organizations,
        isLoading,
        setCurrentOrganization: handleSetCurrentOrganization,
        refetchOrganizations: fetchOrganizations,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization deve ser usado dentro de OrganizationProvider');
  }
  return context;
}
